import React, { useCallback, useState, useEffect } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import { BASE_URL } from './App';

const ACCEPTED_DOC_TYPES = {
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
    '.docx',
  ],
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': [
    '.pptx',
  ],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
    '.xlsx',
  ],
};

const MAX_FILE_SIZE = 200 * 1024 * 1024;
const MAX_FILES = 5;

const normalizeRelatedFiles = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const isAcceptedDoc = (file) => {
  const extension = file.name.split('.').pop()?.toLowerCase() || '';
  return ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'xls', 'xlsx'].includes(extension);
};

export default function DropAllRelatedFile({ relatedFilesData, onSave }) {
  const [relatedFiles, setRelatedFiles] = useState([]);
  const [loaderFlag, setLoaderFlag] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => {
    const normalized = normalizeRelatedFiles(relatedFilesData);
    if (normalized.length > 0 && relatedFiles.length === 0) {
      setRelatedFiles(normalized);
    }
  }, [relatedFilesData]);

  const uploadFile = async (file) => {
    const body = new FormData();
    body.append('image', file, file.name);
    body.append('description', 'operating manual');
    body.append('title', 'related_files');
    body.append('uploadedBy', 'vaibhavi');

    const myHeaders = new Headers();
    myHeaders.append('shared_role', 'admin');
    if (token) {
      myHeaders.append('authorization', 'Bearer ' + token);
    }

    const res = await fetch(`${BASE_URL}/uploads/uiFiles`, {
      method: 'post',
      body: body,
      headers: myHeaders,
    });
    const payload = await res.json();
    if (!res.ok || payload?.status === 'fail' || !payload?.url) {
      throw new Error(payload?.msg || 'Unable to upload file. Please try again.');
    }
    return payload.url;
  };

  const onDrop = useCallback(
    async (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles?.length > 0) {
        const rejection = rejectedFiles[0];
        if (rejection.errors?.some((e) => e.code === 'file-too-large')) {
          toast.error('File is too large. File limit 200 MB.');
        } else if (rejection.errors?.some((e) => e.code === 'too-many-files')) {
          toast.error('Max limit is 5 files.');
        } else {
          toast.error('File format not supported. Use pdf, doc, ppt, or xlsx.');
        }
        return;
      }

      if (acceptedFiles.length === 0) {
        return;
      }

      if (relatedFiles.length + acceptedFiles.length > MAX_FILES) {
        toast.error('Max limit is 5 files.');
        return;
      }

      const invalidFile = acceptedFiles.find((file) => !isAcceptedDoc(file));
      if (invalidFile) {
        toast.error('File format not supported. Use pdf, doc, ppt, or xlsx.');
        return;
      }

      setLoaderFlag(true);
      try {
        for (const file of acceptedFiles) {
          const url = await uploadFile(file);
          setRelatedFiles((prevState) => [
            ...prevState,
            { name: file.name, url },
          ]);
          toast.success(`${file.name} uploaded successfully.`);
        }
      } catch (err) {
        toast.error(err?.message || 'Upload failed. Please try again.');
      } finally {
        setLoaderFlag(false);
      }
    },
    [relatedFiles.length, token]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxFiles: MAX_FILES,
    multiple: true,
    accept: ACCEPTED_DOC_TYPES,
    maxSize: MAX_FILE_SIZE,
    disabled: loaderFlag || relatedFiles.length >= MAX_FILES,
  });

  const handleFileDelete = (file) => {
    setRelatedFiles((prev) => prev.filter((item) => item.url !== file.url));
    toast.success('File removed.');
  };

  useEffect(() => {
    onSave(relatedFiles);
  }, [relatedFiles]);

  return (
    <>
      {relatedFiles.length > 0 && (
        <Row>
          {relatedFiles.map((file, index) => (
            <Col sm={6} key={`${file.url}-${index}`}>
              <div className="upload_related_box">
                <div
                  className="forms-content create-other"
                  style={{ borderBottom: 'none' }}
                >
                  <div className="content-icon-section">
                    <img
                      src={
                        file.name.includes('.docx')
                          ? '../img/doc_blue.svg'
                          : file.name.includes('.pptx')
                          ? '../img/doc_pptx.svg'
                          : '../img/doc_blue.svg'
                      }
                      alt=""
                    />
                  </div>
                  <div className="content-title-section">
                    <h6>{file.name}</h6>
                  </div>
                </div>
                <Button
                  variant="link"
                  onClick={() => {
                    handleFileDelete(file);
                  }}
                >
                  <img src="../../img/removeIcon.svg" alt="Remove" />
                </Button>
              </div>
            </Col>
          ))}
        </Row>
      )}
      <div className="file-upload-form">
        <div
          {...getRootProps({ className: 'dropzone' })}
          style={{ width: '150px', cursor: loaderFlag ? 'wait' : 'pointer' }}
        >
          <input {...getInputProps()} />
          <span className="text-center uploadfile">
            <img
              src={
                loaderFlag
                  ? '../img/mini_loader.gif'
                  : '../img/bi_cloud-upload.png'
              }
              className="me-2"
              alt=""
            />{' '}
            Add Files
          </span>
        </div>
      </div>
    </>
  );
}
